import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Zap, ArrowLeft, ClipboardList, Users, CheckCircle2, Clock, SlidersHorizontal, Search, ChevronDown, ShieldCheck } from "lucide-react";

const POSITIONS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "OF", "UTIL", "DH"];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtDT = (d) => (d ? new Date(d).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export default function EvaluatorCampDashboard() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [camps, setCamps] = useState([]);
  const [campId, setCampId] = useState("");
  const [data, setData] = useState(null);
  const [adminRows, setAdminRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [f, setF] = useState({ q: "", position: "", grad: "", status: "all", number: "" });

  useEffect(() => {
    api.get("/evaluator/camps").then(({ data }) => {
      setCamps(data.items || []);
      if (data.items?.length) setCampId(data.items[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback((id, silent = false) => {
    if (!id) return;
    if (!silent) setLoading(true);
    api.get(`/evaluator/camps/${id}/roster-status`)
      .then(({ data }) => setData(data))
      .catch((e) => { if (!silent) toast.error(formatApiError(e?.response?.data?.detail) || "Could not load camp"); })
      .finally(() => { if (!silent) setLoading(false); });
    if (isAdmin) {
      api.get(`/admin/camps/${id}/evaluator-progress`).then(({ data }) => setAdminRows(data.evaluators || [])).catch(() => setAdminRows([]));
    }
  }, [isAdmin]);

  useEffect(() => { load(campId); }, [campId, load]);

  // Live refresh for camp-day monitoring
  useEffect(() => {
    if (!campId) return;
    const t = setInterval(() => load(campId, true), 15000);
    return () => clearInterval(t);
  }, [campId, load]);

  const gradYears = useMemo(() => {
    if (!data) return [];
    const s = new Set();
    [...data.remaining, ...data.completed].forEach((r) => r.player?.graduation_year && s.add(r.player.graduation_year));
    return [...s].sort();
  }, [data]);

  const match = (row) => {
    const p = row.player || {};
    if (f.q && !`${p.first_name} ${p.last_name}`.toLowerCase().includes(f.q.toLowerCase())) return false;
    if (f.position && p.primary_position !== f.position) return false;
    if (f.grad && String(p.graduation_year) !== f.grad) return false;
    if (f.number && String(row.player_number) !== f.number.replace(/^0+/, "")) return false;
    return true;
  };

  const remaining = (data?.remaining || []).filter(match);
  const completed = (data?.completed || []).filter(match);
  const s = data?.summary || { total: 0, evaluated: 0, remaining: 0, completion_pct: 0 };
  const camp = data?.camp;
  const openQuickEval = (pid) => navigate(`/quick-eval?camp=${campId}&player=${pid}`);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="bg-[#0A192F] text-white px-4 sm:px-6 py-6">
        <div className="max-w-5xl mx-auto">
          <Link to={isAdmin ? "/admin" : "/evaluator"} className="inline-flex items-center gap-2 text-white/50 hover:text-white text-xs uppercase tracking-wider">
            <ArrowLeft className="w-4 h-4" /> {isAdmin ? "Admin" : "Console"}
          </Link>
          <div className="flex items-center gap-2 label-overline text-[#E63946] mt-3"><ClipboardList className="w-4 h-4" /> Evaluator Dashboard</div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter mt-1">{camp?.title || "Select a Camp"}</h1>
          <p className="text-white/50 text-xs mt-1">{camp ? `${fmtDate(camp.start_date)}${camp.city ? " · " + camp.city : ""}${camp.province ? ", " + camp.province : ""}` : "Choose an active camp to see your work queue"}</p>

          <select
            data-testid="camp-selector"
            value={campId}
            onChange={(e) => setCampId(e.target.value)}
            className="mt-4 w-full sm:w-auto bg-white/10 border border-white/20 text-white rounded-sm px-3 py-2.5 text-sm"
          >
            {camps.length === 0 && <option value="">No active camps</option>}
            {camps.map((c) => <option key={c.id} value={c.id} className="text-slate-900">{c.title} ({c.roster_count})</option>)}
          </select>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat testid="summary-total" icon={Users} label="Total Players" value={s.total} />
          <Stat testid="summary-completed" icon={CheckCircle2} label="You Completed" value={s.evaluated} accent="text-emerald-600" />
          <Stat testid="summary-remaining" icon={Clock} label="Remaining" value={s.remaining} accent="text-[#E63946]" />
          <Stat testid="summary-pct" icon={ClipboardList} label="Completion" value={`${s.completion_pct}%`} />
        </div>
        <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${s.completion_pct}%` }} data-testid="dashboard-progress-bar" />
        </div>

        {/* Filters */}
        <div className="mt-6">
          <button data-testid="filter-toggle" onClick={() => setShowFilters((v) => !v)} className="sm:hidden w-full flex items-center justify-between bg-white border border-slate-200 rounded-sm px-4 py-2.5 text-sm font-semibold uppercase tracking-wider">
            <span className="flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Filters</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
          </button>
          <div className={`${showFilters ? "grid" : "hidden"} sm:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mt-2 sm:mt-0`}>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <input data-testid="filter-search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search name…" className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </div>
            <input data-testid="filter-number" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} inputMode="numeric" placeholder="Player #" className="px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            <select data-testid="filter-position" value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="">All positions</option>
              {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select data-testid="filter-grad" value={f.grad} onChange={(e) => setF({ ...f, grad: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="">All grad years</option>
              {gradYears.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <select data-testid="filter-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="all">All</option>
              <option value="remaining">Remaining only</option>
              <option value="completed">Completed only</option>
            </select>
          </div>
        </div>

        {loading && <div className="mt-6 label-overline text-slate-400">Loading…</div>}

        {/* Remaining work queue */}
        {f.status !== "completed" && (
          <section className="mt-6">
            <h2 className="label-overline text-[#E63946] flex items-center gap-2"><Clock className="w-4 h-4" /> Players Remaining ({remaining.length})</h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {remaining.map((r) => {
                const p = r.player || {};
                return (
                  <div key={r.player_id} data-testid={`remaining-player-${r.player_id}`} className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-3">
                    <div className="font-data text-2xl text-[#0A192F] w-12 text-center shrink-0">{r.player_number != null ? String(r.player_number).padStart(3, "0") : "—"}</div>
                    <div className="w-11 h-11 rounded-sm bg-slate-100 overflow-hidden shrink-0">
                      {p.profile_photo_url ? <img src={p.profile_photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-300 font-display">{(p.first_name || "?")[0]}</div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{p.first_name} {p.last_name}</div>
                      <div className="text-[11px] text-slate-500">{[p.primary_position, p.graduation_year, p.province].filter(Boolean).join(" · ")}{p.cbr_overall_rating != null ? ` · CBR ${Number(p.cbr_overall_rating).toFixed(1)}` : ""}</div>
                    </div>
                    <button data-testid={`quick-eval-btn-${r.player_id}`} onClick={() => openQuickEval(r.player_id)} className="shrink-0 inline-flex items-center gap-1 bg-[#E63946] hover:bg-[#D90429] text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                      <Zap className="w-3.5 h-3.5" /> Eval
                    </button>
                  </div>
                );
              })}
              {remaining.length === 0 && <p className="text-sm text-slate-500">No remaining players match your filters. 🎉</p>}
            </div>
          </section>
        )}

        {/* Completed */}
        {f.status !== "remaining" && (
          <section className="mt-8">
            <h2 className="label-overline text-emerald-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Completed by You ({completed.length})</h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {completed.map((r) => {
                const p = r.player || {};
                return (
                  <div key={r.player_id} data-testid={`completed-player-${r.player_id}`} className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-3">
                    <div className="font-data text-xl text-slate-400 w-12 text-center shrink-0">{r.player_number != null ? String(r.player_number).padStart(3, "0") : "—"}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{p.first_name} {p.last_name}</div>
                      <div className="text-[11px] text-slate-500">{[p.primary_position, p.graduation_year].filter(Boolean).join(" · ")} · {fmtDT(r.submitted_at)}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[9px] uppercase tracking-wider text-slate-400">OFV</div>
                      <div className="font-data text-2xl text-emerald-600 leading-none">{r.overall_future_value ?? "—"}</div>
                    </div>
                  </div>
                );
              })}
              {completed.length === 0 && <p className="text-sm text-slate-500">No completed evaluations yet.</p>}
            </div>
          </section>
        )}

        {/* Admin: per-evaluator progress */}
        {isAdmin && (
          <section className="mt-10" data-testid="admin-evaluator-progress">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="label-overline text-[#0A192F] flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Evaluator Progress (Camp Leadership)</h2>
              <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-wider text-slate-500">
                <StatusLegend />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {adminRows.map((e) => (
                <div key={e.evaluator_id} data-testid={`evaluator-progress-${e.evaluator_id}`} className="bg-white border border-slate-200 rounded-sm p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm truncate">{e.evaluator_name}</span>
                    <StatusBadge status={e.status} testid={`evaluator-status-${e.evaluator_id}`} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{e.completed}/{e.completed + e.remaining} done · {e.remaining} remaining · <span className="font-semibold text-[#0A192F]">{e.completion_pct}%</span>{e.last_activity ? ` · last ${fmtDT(e.last_activity)}` : ""}</div>
                  <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-[#0A192F]" style={{ width: `${e.completion_pct}%` }} />
                  </div>
                </div>
              ))}
              {adminRows.length === 0 && <p className="text-sm text-slate-500">No evaluators found for this camp.</p>}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent = "text-[#0A192F]", testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center gap-2 label-overline text-[9px]"><Icon className="w-3.5 h-3.5 text-slate-400" /> {label}</div>
      <div className={`font-data text-3xl mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

const STATUS_META = {
  available: { label: "Available", dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", pulse: false },
  evaluating: { label: "Evaluating", dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50", pulse: true },
  finished: { label: "Finished", dot: "bg-[#0A192F]", text: "text-[#0A192F]", bg: "bg-slate-100", pulse: false },
  idle: { label: "Idle", dot: "bg-slate-400", text: "text-slate-500", bg: "bg-slate-50", pulse: false },
};

function StatusBadge({ status, testid }) {
  const m = STATUS_META[status] || STATUS_META.idle;
  return (
    <span data-testid={testid} data-status={status} className={`shrink-0 inline-flex items-center gap-1.5 ${m.bg} ${m.text} px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wider`}>
      <span className={`relative flex w-2 h-2`}>
        {m.pulse && <span className={`absolute inline-flex w-full h-full rounded-full ${m.dot} opacity-60 animate-ping`} />}
        <span className={`relative inline-flex w-2 h-2 rounded-full ${m.dot}`} />
      </span>
      {m.label}
    </span>
  );
}

function StatusLegend() {
  return (
    <>
      {Object.entries(STATUS_META).map(([k, m]) => (
        <span key={k} className="inline-flex items-center gap-1">
          <span className={`w-2 h-2 rounded-full ${m.dot}`} /> {m.label}
        </span>
      ))}
    </>
  );
}
